import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, CheckCircle2, XCircle, MinusCircle, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { summarizeWhatsAppAuditDetail } from "@/lib/whatsappAudit";
import { useIsMobile } from "@/hooks/useMobile";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const MESSAGE_TYPE_LABELS: Record<string, string> = {
  confirmation: "Confirmação",
  cancellation: "Cancelamento",
  reminder_24h: "Lembrete 24h",
  reminder_1h: "Lembrete 1h",
  review_request: "Avaliação",
  reschedule: "Reagendamento",
  reactivation: "Reativação",
  campaign: "Campanha",
  other: "Outro",
};

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "destructive" | "secondary"; icon: React.ReactNode }> = {
  sent: { label: "Enviado", variant: "default", icon: <CheckCircle2 className="w-3 h-3" /> },
  failed: { label: "Falhou", variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
  skipped: { label: "Ignorado", variant: "secondary", icon: <MinusCircle className="w-3 h-3" /> },
};

const PAGE_SIZE = 50;
const AUDIT_STATUS_COLORS = ["#16a34a", "#dc2626", "#ca8a04"];

export default function WhatsAppLogsPage() {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(0);
  const [messageType, setMessageType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [clientPhone, setClientPhone] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [days, setDays] = useState(30);

  const { data: stats, isLoading: statsLoading } = trpc.whatsappLogs.stats.useQuery({ days });
  const { data: logs, isLoading: logsLoading, refetch } = trpc.whatsappLogs.list.useQuery({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    messageType: messageType as any || undefined,
    status: status as any || undefined,
    clientPhone: phoneFilter || undefined,
    days,
  });

  const totalPages = logs ? Math.ceil(logs.total / PAGE_SIZE) : 0;
  const periodLabel = days === 1 ? "últimas 24h" : `últimos ${days} dias`;
  const statusChartData = [
    { name: "Enviados", value: stats?.sent ?? 0 },
    { name: "Falhas", value: stats?.failed ?? 0 },
    { name: "Ignorados", value: stats?.skipped ?? 0 },
  ].filter((item) => item.value > 0);
  const typeChartData = Object.entries(stats?.byType ?? {})
    .map(([type, count]) => ({ name: MESSAGE_TYPE_LABELS[type] ?? type, value: Number(count) }))
    .sort((a, b) => b.value - a.value);

  function applyPhoneFilter() {
    setPhoneFilter(clientPhone);
    setPage(0);
  }

  function clearFilters() {
    setMessageType("");
    setStatus("");
    setClientPhone("");
    setPhoneFilter("");
    setPage(0);
  }

  return (
    <div className={isMobile ? "p-3 space-y-4" : "p-6 space-y-6"}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Auditoria WhatsApp</h1>
          <p className="text-muted-foreground text-sm mt-1">Histórico de todas as mensagens enviadas pelo sistema</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {!statsLoading && stats && (
        <div className={isMobile ? "grid grid-cols-2 gap-3" : "grid grid-cols-2 md:grid-cols-4 gap-4"}>
          <Card>
            <CardContent className={isMobile ? "p-3" : "pt-4 pb-4"}>
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-xs text-muted-foreground">Total ({days === 1 ? "24h" : `${days} dias`})</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className={isMobile ? "p-3" : "pt-4 pb-4"}>
              <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
              <div className="text-xs text-muted-foreground">Enviados</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className={isMobile ? "p-3" : "pt-4 pb-4"}>
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-xs text-muted-foreground">Falhas</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className={isMobile ? "p-3" : "pt-4 pb-4"}>
              <div className="text-2xl font-bold text-yellow-600">{stats.skipped}</div>
              <div className="text-xs text-muted-foreground">Ignorados (opt-out)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breakdown por tipo */}
      {!statsLoading && stats && Object.keys(stats.byType).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Mensagens por tipo ({days === 1 ? "24h" : `${days} dias`})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} className="flex items-center gap-1.5 bg-muted rounded-md px-3 py-1.5">
                  <span className="text-xs font-medium">{MESSAGE_TYPE_LABELS[type] ?? type}</span>
                  <Badge variant="secondary" className="text-xs h-5">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!statsLoading && stats && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-sm font-medium">Situação dos envios</CardTitle>
              <p className="text-xs text-muted-foreground">Distribuição no período selecionado: {periodLabel}</p>
            </CardHeader>
            <CardContent className="pt-3">
              {statusChartData.length ? (
                <div className="h-56" aria-label={`Gráfico da situação dos envios nas ${periodLabel}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusChartData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={3}>
                        {statusChartData.map((entry, index) => <Cell key={entry.name} fill={AUDIT_STATUS_COLORS[index]} />)}
                      </Pie>
                      <Tooltip formatter={(value: number) => [`${value} mensagem${value === 1 ? "" : "ns"}`, "Quantidade"]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">Sem mensagens no período selecionado.</div>
              )}
              {statusChartData.length > 0 && (
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
                  {statusChartData.map((item, index) => (
                    <span key={item.name} className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: AUDIT_STATUS_COLORS[index] }} />
                      {item.name}: <strong>{item.value}</strong>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-sm font-medium">Volume por tipo de mensagem</CardTitle>
              <p className="text-xs text-muted-foreground">Tipos disparados no período selecionado: {periodLabel}</p>
            </CardHeader>
            <CardContent className="pt-3">
              {typeChartData.length ? (
                <div className="h-56" aria-label={`Gráfico de mensagens por tipo nos ${periodLabel}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={typeChartData} margin={{ top: 8, right: 8, left: -20, bottom: 4 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={48} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value: number) => [`${value} mensagem${value === 1 ? "" : "ns"}`, "Quantidade"]} />
                      <Bar dataKey="value" name="Mensagens" fill="#a16207" radius={[5, 5, 0, 0]} maxBarSize={38} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">Sem mensagens no período selecionado.</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className={isMobile ? "grid grid-cols-1 gap-3" : "flex flex-wrap gap-3 items-end"}>
            <div className={isMobile ? "w-full" : "flex-1 min-w-[180px]"}>
              <label className="text-xs text-muted-foreground mb-1 block">Tipo de mensagem</label>
              <Select value={messageType} onValueChange={(v) => { setMessageType(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {Object.entries(MESSAGE_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={isMobile ? "w-full" : "flex-1 min-w-[140px]"}>
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <Select value={status} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="failed">Falhou</SelectItem>
                  <SelectItem value="skipped">Ignorado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className={isMobile ? "w-full" : "min-w-[140px]"}>
              <label className="text-xs text-muted-foreground mb-1 block">Período</label>
              <Select value={String(days)} onValueChange={(v) => { setDays(Number(v)); setPage(0); }}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Últimas 24h</SelectItem>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className={isMobile ? "w-full" : "flex-1 min-w-[180px]"}>
              <label className="text-xs text-muted-foreground mb-1 block">Telefone do cliente</label>
              <div className="flex gap-2">
                <Input
                  className="h-9"
                  placeholder="Ex: 71999990000"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyPhoneFilter()}
                />
                <Button size="sm" className="h-9 px-3" onClick={applyPhoneFilter}>Buscar</Button>
              </div>
            </div>
            {(messageType || status || phoneFilter) && (
              <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabela de logs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Histórico de mensagens
            {logs && <span className="text-muted-foreground font-normal">({logs.total} registros)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Carregando...</div>
          ) : !logs?.data.length ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              Nenhuma mensagem encontrada com os filtros selecionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 pr-4 font-medium">Data/Hora</th>
                    <th className="text-left py-2 pr-4 font-medium">Cliente</th>
                    <th className="text-left py-2 pr-4 font-medium">Telefone</th>
                    <th className="text-left py-2 pr-4 font-medium">Tipo</th>
                    <th className="text-left py-2 pr-4 font-medium">Status</th>
                    <th className="text-left py-2 font-medium">Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.data.map((log) => {
                    const statusCfg = STATUS_CONFIG[log.status] ?? STATUS_CONFIG.sent;
                    const detail = log.status === "failed" || log.status === "skipped"
                      ? summarizeWhatsAppAuditDetail(log.status, log.errorReason)
                      : log.messagePreview
                        ? log.messagePreview.replace(/[*_~]/g, "").slice(0, 80) + (log.messagePreview.length > 80 ? "…" : "")
                        : "—";
                    return (
                      <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="py-2.5 pr-4 whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(log.sentAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                        </td>
                        <td className="py-2.5 pr-4 font-medium">{log.clientName || "—"}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground font-mono text-xs">{log.clientPhone}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant="outline" className="text-xs">
                            {MESSAGE_TYPE_LABELS[log.messageType] ?? log.messageType}
                          </Badge>
                        </td>
                        <td className="py-2.5 pr-4">
                          <Badge variant={statusCfg.variant} className="text-xs flex items-center gap-1 w-fit">
                            {statusCfg.icon}
                            {statusCfg.label}
                          </Badge>
                        </td>
                        <td
                          className={`py-2.5 text-xs max-w-[320px] truncate ${log.status === "failed" ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
                          title={log.errorReason ?? undefined}
                        >
                          {detail}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-xs text-muted-foreground">
                Página {page + 1} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
